import { Card } from '../../Card';
import AdminHeader from '../admin/AdminHeader';
import { useState, useCallback, useMemo } from 'react';
import Loading from 'src/components/Loading';
import { useTableData, PaginationParams, PaginatedResponse, SortDirection } from 'src/components/table/useTableData';
import { apiGet } from '../../../utils/api';
import { useCurrentOrganization } from 'src/contexts/OrganizationContext';
import ActionsMenu from '../../ActionsMenu';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import Hl7LogDetailModal from 'src/components/modals/Hl7LogDetailModal';
import TableDateTime from 'src/components/table/TableDateTime';
import { Hl7Log } from 'src/shared/types';
import Header from 'src/header';
import { Table } from 'src/components/table/Table';
import { ColumnProps } from 'src/components/table/Table';

const Hl7LogsPage = () => {
  const organization = useCurrentOrganization();
  const [detailLog, setDetailLog] = useState<Hl7Log | null>(null);

  const getHl7Logs = useCallback(
    async (params: PaginationParams): Promise<PaginatedResponse<Record<string, unknown>>> => {
      const response = await apiGet(`/api/${organization?.id}/hl7-logs`, {
        page: params.page,
        pageSize: params.pageSize,
        sortProperty: params.sortProperty,
        sortDirection: params.sortDirection,
      });
      const data = await response.json();
      const items = (data.items ?? []).map((log: Hl7Log) => ({
        ...log,
        Actions: (
          <ActionsMenu
            actions={[
              { id: 'advanced-details', label: 'Advanced details', onClick: () => setDetailLog(log) },
            ]}
          />
        ),
      }));
      return { ...data, items };
    },
    [organization?.id]
  );

  const tableData = useTableData<Record<string, unknown>>({
    tableId: 'hl7-logs',
    getData: getHl7Logs,
    initialSortField: 'created_at',
    initialSortDirection: SortDirection.DESC,
    updateUrlParams: true,
  });

  const columns: ColumnProps<Record<string, unknown>>[] = useMemo(() => [
    {
      title: 'Call Type',
      convertToCellData: (row) => (
        <span className="text-sm text-gray-900">{(row as Hl7Log).call_type}</span>
      ),
      sortKey: 'call_type',
    },
    {
      title: 'Date & Time',
      convertToCellData: (row) => {
        const log = row as Hl7Log;
        const d = new Date(log.created_at);
        const dateStr = d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
        const timeStr = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
        return <TableDateTime date={dateStr} startTime={timeStr} />;
      },
      sortKey: 'created_at',
    },
    {
      title: 'Patient ID',
      convertToCellData: (row) => (
        <span className="text-sm text-gray-900 font-mono">{(row as Hl7Log).patient_id}</span>
      ),
      sortKey: 'patient_id',
    },
    {
      title: 'Agent Email',
      convertToCellData: (row) => (
        <span className="text-sm text-gray-900">{(row as Hl7Log).agent_email ?? '—'}</span>
      ),
      sortKey: 'agent_email',
    },
  ], []);

  return (
    <div className="min-h-screen bg-gray-100">
      <Header isAdminPage={true} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <AdminHeader
          title="HL7 Logs"
          description="View HL7 message and ACK details for document and clinical note uploads"
          organization={organization}
        />

        <Card className="w-full p-8">
          <div className="flex justify-end mb-4">
            <button
              type="button"
              onClick={() => tableData.refresh()}
              disabled={tableData.loading}
              className="inline-flex items-center gap-2 rounded-lg border border-brand-600 bg-white p-2 sm:px-4 sm:py-2 text-sm font-medium text-brand-600 hover:bg-brand-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-5 w-5 ${tableData.loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
          {tableData.loading ? (
            <Loading message="Loading HL7 logs..." />
          ) : tableData.items && tableData.items.length > 0 ? (
            <Table
              tableData={tableData}
              rowId={(item) => String(item.id)}
              columns={columns}
              trailingColumnKey="Actions"
              headerClass="bg-gray-50"
              headerColor={false}
            />
          ) : (
            <div className="text-center space-y-4 py-12">
              <p className="text-gray-500 text-lg">No HL7 logs yet</p>
              <p className="text-gray-500 text-sm">Logs appear here when you upload documents or clinical notes via HL7.</p>
            </div>
          )}
        </Card>

        {detailLog != null && (
          <Hl7LogDetailModal
            log={detailLog}
            onClose={() => setDetailLog(null)}
          />
        )}
      </div>
    </div>
  );
};

export default Hl7LogsPage;
